import React from 'react';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { Alert } from '../types';

interface AutoFixPageProps {
  currentPage: string;
  activeAlertsCount: number;
  selectedAlert: Alert;
  alertAnalysis: any;
  onNavigate: (page: string) => void;
  onLogout: () => void;
  onGoBackToAlertDetails: () => void;
}

export const AutoFixPage: React.FC<AutoFixPageProps> = ({
  currentPage,
  activeAlertsCount,
  selectedAlert,
  alertAnalysis,
  onNavigate,
  onLogout,
  onGoBackToAlertDetails,
}) => {
  return (
    <div className="h-screen bg-[#1A1A1A] text-white flex overflow-hidden">
      <Sidebar
        currentPage={currentPage}
        activeAlertsCount={activeAlertsCount}
        onNavigate={onNavigate}
        onLogout={onLogout}
      />
      <div className="flex-1 ml-64 p-8 overflow-y-auto">
        <button
          onClick={onGoBackToAlertDetails}
          className="flex items-center space-x-2 text-[#8b949e] hover:bg-[#1A1A1A] hover:text-gray-200 mb-4 px-3 py-2 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Alert Details</span>
        </button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">
              Recommended Code Changes
            </h1>
            <p className="text-[#8b949e]">
              {alertAnalysis?.recommended_actions?.length || 0} Changes • {selectedAlert.title}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-green-400">
              Review Changes
            </div>
            <div className="text-sm text-[#8b949e]">
              From Analysis
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* PR Summary */}
            <div className="bg-[#222222] rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 text-white">Pull Request Summary</h2>
              <div className="space-y-4">
                <div className="border border-gray-600 rounded-lg p-4">
                  <h3 className="font-semibold text-white mb-2">Fix: {selectedAlert.title}</h3>
                  <p className="text-sm text-[#8b949e] mb-3">
                    This PR addresses the {selectedAlert.annotations.description} issue in the {selectedAlert.service_name} service.
                  </p>
                  {alertAnalysis?.recommended_actions && (
                    <div className="flex items-center space-x-4 text-sm">
                      <span className="text-[#8b949e]">
                        {alertAnalysis.recommended_actions.length} {alertAnalysis.recommended_actions.length === 1 ? 'file' : 'files'} changed
                      </span>
                      <span className="text-[#8b949e]">
                        {alertAnalysis.recommended_actions.filter((a: any) => a.priority === 1).length} priority actions
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Enhanced Code Changes */}
            <div className="bg-[#222222] rounded-lg p-6">
              <h2 className="text-xl font-semibold mb-4 text-white">Detailed Code Changes</h2>

              {/* File Changes */}
              <div className="space-y-6">
                {/* Dynamic code changes from API */}
                {alertAnalysis?.recommended_actions && (
                  <>
                    {alertAnalysis.recommended_actions.map((action: any, index: number) => (
                      action.implementation?.change && (
                        <div key={index} className="bg-black/30 border border-gray-600 rounded-lg overflow-hidden">
                          <div className="bg-[#222222] px-4 py-2 border-b border-gray-600">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium">{action.implementation.file}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  action.priority === 1
                                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                                }`}>
                                  {action.priority === 1 ? 'Week 1' : 'Week 2-3'}
                                </span>
                              </div>
                            </div>
                            <div className="text-sm text-gray-400 mt-1">{action.title}</div>
                          </div>
                          <div className="p-4 font-mono text-sm overflow-x-auto">
                            <div className="space-y-1">
                              {(() => {
                                const lines = action.implementation.change.split('\n');
                                let inCurrentSection = false;
                                let inChangeToSection = false;

                                return lines.map((line: string, i: number) => {
                                  // Check if we're entering a new section
                                  if (line.includes('// Current')) {
                                    inCurrentSection = true;
                                    inChangeToSection = false;
                                  } else if (line.includes('// Change To:') || line.includes('// Add Before')) {
                                    inCurrentSection = false;
                                    inChangeToSection = true;
                                  } else if (line.trim() === '' && inCurrentSection) {
                                    // Empty line ends current section
                                    inCurrentSection = false;
                                  }

                                  const isHeaderComment = line.includes('// Current') || line.includes('// Change To:') || line.includes('// Add Before');
                                  const isComment = line.trim().startsWith('//') && !isHeaderComment;

                                  return (
                                    <div key={i} className={`whitespace-pre ${
                                      inCurrentSection ? 'bg-red-500/20 text-red-300 pl-2' :
                                      inChangeToSection ? 'bg-green-500/20 text-green-300 pl-2' :
                                      isComment ? 'text-gray-400' :
                                      'text-gray-300'
                                    }`}>
                                      {line}
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </div>
                        </div>
                      )
                    ))}
                  </>
                )}
              </div>

              {/* PR Summary */}
              <div className="mt-6 bg-white/5 border border-white/10 rounded-lg p-4">
                <h5 className="text-white font-semibold mb-3">📋 Changes Summary</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-400 mb-2">Files Changed:</div>
                    <div className="text-white">
                      {alertAnalysis?.recommended_actions?.length || 0} files
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 mb-2">Phase:</div>
                    <div className="text-white">
                      Week 1-3 Implementation
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 mb-2">Priority Actions:</div>
                    <div className="text-white">
                      {alertAnalysis?.recommended_actions?.filter((a: any) => a.priority === 1).length || 0} immediate
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 mb-2">Risk Level:</div>
                    <div className="text-green-400">
                      Low
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="lg:col-span-1 space-y-6">
            {/* Actions */}
            <div className="bg-[#222222] rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-white">Actions</h3>
              <div className="space-y-3">
                <button
                  disabled
                  className="w-full bg-gray-600 px-4 py-3 rounded-lg text-sm flex items-center justify-center space-x-2 cursor-not-allowed opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  <span>Create Pull Request</span>
                </button>
                <button
                  disabled
                  className="w-full bg-gray-600 px-4 py-3 rounded-lg text-sm flex items-center justify-center space-x-2 cursor-not-allowed opacity-50"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                  </svg>
                  <span>View on GitHub</span>
                </button>
              </div>
            </div>

            {/* Implementation Notes */}
            <div className="bg-[#222222] rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4 text-white">Implementation Notes</h3>
              <div className="space-y-3">
                <div className="text-sm text-[#8b949e]">
                  <strong>Risk Level:</strong> Low
                </div>
                <div className="text-sm text-[#8b949e]">
                  <strong>Testing:</strong> Manual validation recommended
                </div>
                <div className="text-sm text-[#8b949e]">
                  <strong>Rollback:</strong> Standard deployment process
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
